import { lazy, Suspense } from "react"
import { useParams } from "react-router-dom"
import { getToolBySlug } from "./toolRegistry"

// Lazy-load each built tool page
const PAGE_MAP: Record<string, React.LazyExoticComponent<() => React.ReactElement>> = {
  "rental-yield-calculator": lazy(() => import("./calculators/RentalYieldCalculatorPage")),
  "prorated-rent-calculator": lazy(() => import("./calculators/ProratedRentCalculatorPage")),
  "rent-increase-calculator": lazy(() => import("./calculators/RentIncreaseCalculatorPage")),
  "one-percent-rule-checker": lazy(() => import("./calculators/OnePercentRuleCheckerPage")),
  "vacancy-rate-cost-calculator": lazy(() => import("./calculators/VacancyRateCostCalculatorPage")),
  "break-even-rent-calculator": lazy(() => import("./calculators/BreakEvenRentCalculatorPage")),
  "late-fee-calculator": lazy(() => import("./calculators/LateFeeCalculatorPage")),
  "security-deposit-interest-calculator": lazy(() => import("./calculators/SecurityDepositInterestCalculatorPage")),
  "price-to-rent-ratio-calculator": lazy(() => import("./calculators/PriceToRentRatioCalculatorPage")),
  "cash-flow-calculator": lazy(() => import("./calculators/CashFlowCalculatorPage")),
  "maintenance-budget-estimator": lazy(() => import("./calculators/MaintenanceBudgetEstimatorPage")),
  "property-management-fee-calculator": lazy(() => import("./calculators/PropertyManagementFeeCalculatorPage")),
  "debt-to-income-calculator": lazy(() => import("./calculators/DebtToIncomeCalculatorPage")),
  "utilities-cost-splitter": lazy(() => import("./calculators/UtilitiesCostSplitterPage")),
  "mortgage-affordability-calculator": lazy(() => import("./calculators/MortgageAffordabilityCalculatorPage")),
  "roi-calculator": lazy(() => import("./calculators/RoiCalculatorPage")),
  // v7-03 complex tools
  "stamp-duty-calculator": lazy(() => import("./calculators/StampDutyCalculatorPage")),
  "buy-vs-rent-calculator": lazy(() => import("./calculators/BuyVsRentCalculatorPage")),
  "depreciation-calculator": lazy(() => import("./calculators/DepreciationCalculatorPage")),
  "lease-expiry-tracker": lazy(() => import("./calculators/LeaseExpiryTrackerPage")),
  "capital-gains-tax-estimator": lazy(() => import("./calculators/CapitalGainsTaxEstimatorPage")),
}

function ToolSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-slate-200 rounded-lg w-2/3" />
      <div className="h-4 bg-slate-200 rounded w-1/2" />
      <div className="grid md:grid-cols-2 gap-6">
        <div className="h-64 bg-slate-200 rounded-2xl" />
        <div className="h-64 bg-slate-200 rounded-2xl" />
      </div>
    </div>
  )
}

function ComingSoon({ slug }: { slug: string }) {
  const tool = getToolBySlug(slug)
  return (
    <div className="flex flex-col items-center justify-center py-24 text-center">
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-slate-100 mb-6">
        {tool?.icon && <tool.icon className="w-8 h-8 text-slate-400" />}
      </div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">
        {tool ? tool.title : "Tool Not Found"}
      </h2>
      <p className="text-slate-500 max-w-sm">
        {tool
          ? "This tool is currently being built. Check back soon — we're adding new tools regularly."
          : "This tool does not exist. Browse all available tools below."}
      </p>
      <a
        href="/tools"
        className="mt-6 inline-flex items-center px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 transition-colors"
      >
        Browse all tools
      </a>
    </div>
  )
}

export default function ToolDynamicRouter() {
  const { slug } = useParams<{ slug: string }>()

  if (!slug) return <ComingSoon slug="" />

  const PageComponent = PAGE_MAP[slug]

  if (!PageComponent) {
    return <ComingSoon slug={slug} />
  }

  return (
    <Suspense fallback={<ToolSkeleton />}>
      <PageComponent />
    </Suspense>
  )
}
